import Modal from "@/components/controls/Modal";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import FileUpload from "@/components/controls/FileUpload/FileUpload";
import { SavedAttachmentInfo } from "@/entities/attachments/types";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";
import {
  createFormNewSectionsFromAPI,
  CreateFormNewContext
} from "@/utils/entityFieldsUtils/formFields";
import { getUsersOptions } from "@/entities/forms/helpers";
import { useEntityFields } from "@/pages/settings/hooks";
import { useEnablePartnerships } from "@/pages/partners/hooks";
import { excludePartnerFields } from "@/entities/dataTables/helpers";
import UserAvatar from "@/components/controls/UserAvatar";
import { useProducts } from "@/entities/settings/hooks";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { UserData } from "@/entities/users/types";
import { LeadType } from "@/pages/leads/types";
import {
  useLeadInboundCampaignTypes,
  useLeadOutboundCampaignTypes
} from "../../../hooks";
import useBoundStore from "@/services/store/BoundStore";
import { isEmpty, map } from "lodash";
import { useEntitySelect } from "@/services/hooks/entities/useEntitySelect";
import { getCampaignTypeOptions } from "@/pages/leads/components/LeadDetailFields/utils";
import { useImportLeadsMutation, useImportLeadsStatus } from "../hooks";
import toast from "react-hot-toast";
import { ImportLeadsData } from "../types";
import ImportProcessingContent from "./ImportProcessingContent";

const TRANSLATION_KEY = "leads.importModal";
const IMPORT_ID_STORAGE_KEY = "leads_import_id";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  userData: UserData;
}

const ImportLeadsModal: React.FC<Props> = ({ isOpen, setIsOpen, userData }) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const uploadedFileRef = useRef<File | null>(null);
  const [attachments, setAttachments] = useState<SavedAttachmentInfo[]>([]);
  const [hasFile, setHasFile] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [productId, setProductId] = useState<string>("");
  const [formSetValue, setFormSetValue] = useState<any>(null);
  const [formGetValues, setFormGetValues] = useState<any>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [selectedSource, setSelectedSource] = useState<LeadType | null>(null);
  const [selectedReferringCompanyId, setSelectedReferringCompanyId] =
    useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentImportId, setCurrentImportId] = useState<string>("");

  const products = useProducts().data?.products ?? [];
  const users = useBoundStore((state) => state.users.entities);
  const inboundCampaignTypes =
    useLeadInboundCampaignTypes().data?.inboundCampaignTypes ?? [];
  const outboundCampaignTypes =
    useLeadOutboundCampaignTypes().data?.outboundCampaignTypes ?? [];
  const { data: entityFieldsResponse } = useEntityFields(EntityType.LEAD);
  const enablePartnerships = useEnablePartnerships();
  const visibleFields = useMemo(
    () =>
      enablePartnerships
        ? entityFieldsResponse?.fields
        : excludePartnerFields(entityFieldsResponse?.fields ?? []),
    [entityFieldsResponse?.fields, enablePartnerships]
  );

  const importLeadsMutation = useImportLeadsMutation();
  const importStatus = useImportLeadsStatus(currentImportId, {
    enabled: Boolean(currentImportId) && isProcessing
  });

  const {
    options: referringCompanyOptions,
    onSearch: onReferringCompanySearch,
    isLoading: isLoadingReferringCompanies,
    searchTerm: referringCompanySearchTerm
  } = useEntitySelect({
    entityType: EntityType.COMPANY,
    alwaysEnabled: selectedSource === LeadType.REFERRAL
  });

  const {
    options: referringPersonOptions,
    onSearch: onReferringPersonSearch,
    isLoading: isLoadingReferringPeople,
    searchTerm: referringPersonSearchTerm
  } = useEntitySelect({
    entityType: EntityType.PERSON,
    filters: { companyId: selectedReferringCompanyId }
  });

  const getInboundCampaignTypeOptions = () =>
    getCampaignTypeOptions(inboundCampaignTypes);
  const getOutboundCampaignTypeOptions = () =>
    getCampaignTypeOptions(outboundCampaignTypes);

  const getConditionalFields = useMemo((): FormFieldNewType[] => {
    const conditionalFields: FormFieldNewType[] = [];

    if (selectedSource === LeadType.INBOUND) {
      conditionalFields.push({
        name: "inboundCampaignType",
        label: t("leads.createModal.inboundCampaignType"),
        type: "singleSelect" as const,
        required: false,
        isDependentRequired: true,
        labelPosition: "left" as const,
        fieldActionProps: {
          fieldName: "inboundCampaignType",
          options: getInboundCampaignTypeOptions()
        }
      });
    }

    if (selectedSource === LeadType.OUTBOUND) {
      conditionalFields.push({
        name: "outboundCampaignType",
        label: t("leads.createModal.outboundCampaignType"),
        type: "singleSelect" as const,
        required: false,
        isDependentRequired: true,
        labelPosition: "left" as const,
        fieldActionProps: {
          fieldName: "outboundCampaignType",
          options: getOutboundCampaignTypeOptions()
        }
      });
    }

    if (selectedSource === LeadType.REFERRAL) {
      conditionalFields.push({
        name: "referringCompany",
        label: t("leads.createModal.referringCompany"),
        type: "singleSelect" as const,
        required: false,
        isDependentRequired: true,
        labelPosition: "left" as const,
        fieldActionProps: {
          fieldName: "referringCompany",
          options: referringCompanyOptions,
          showSearch: true,
          initialSearchTerm: referringCompanySearchTerm,
          onSearch: onReferringCompanySearch,
          isLoading: isLoadingReferringCompanies,
          onSelection: (value: any) => {
            setSelectedReferringCompanyId(value as string);
            // Clear referring person when company changes
            if (typeof formSetValue === "function") {
              formSetValue("referringPerson", "", {
                shouldValidate: true
              });
            }
          }
        }
      });
    }

    if (!isEmpty(selectedReferringCompanyId)) {
      conditionalFields.push({
        name: "referringPerson",
        label: t("leads.createModal.referringPerson"),
        type: "singleSelect" as const,
        labelPosition: "left" as const,
        required: false,
        fieldActionProps: {
          fieldName: "referringPerson",
          options: referringPersonOptions,
          showSearch: true,
          initialSearchTerm: referringPersonSearchTerm,
          onSearch: onReferringPersonSearch,
          isLoading: isLoadingReferringPeople
        }
      });
    }

    return conditionalFields;
  }, [
    selectedSource,
    selectedReferringCompanyId,
    referringCompanyOptions,
    referringPersonOptions,
    referringCompanySearchTerm,
    referringPersonSearchTerm,
    isLoadingReferringCompanies,
    isLoadingReferringPeople,
    formSetValue,
    t
  ]);

  const handleClose = () => {
    setIsOpen(false);
    setAttachments([]);
    uploadedFileRef.current = null;
    setHasFile(false);
    setFileError(null);
    setProductId("");
    setSelectedSource(null);
    setSelectedReferringCompanyId("");
    setIsFormValid(false);
    formRef.current?.reset();
  };

  const getHasConditionalFieldsBeenFilled = (
    watchAllFields: Record<string, any>
  ) => {
    if (selectedSource === LeadType.INBOUND) {
      return Boolean(watchAllFields?.inboundCampaignType);
    }
    if (selectedSource === LeadType.OUTBOUND) {
      return Boolean(watchAllFields?.outboundCampaignType);
    }
    if (selectedSource === LeadType.REFERRAL) {
      return Boolean(watchAllFields?.referringCompany);
    }
    return true;
  };

  const handleImport = async () => {
    if (!uploadedFileRef.current) {
      toast.error(t(`${TRANSLATION_KEY}.noFileError`));
      return;
    }

    setFileError(null);

    const formData = formGetValues?.() || {};

    const uploadFormData = new FormData();

    const file = uploadedFileRef.current;

    uploadFormData.append("file", file, file.name);

    const queryParams: Partial<ImportLeadsData> = {};

    Object.entries(formData).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (
          key === "source" ||
          key === "inboundCampaignType" ||
          key === "outboundCampaignType"
        ) {
          queryParams[key as keyof ImportLeadsData] = Number(value) as any;
        } else {
          queryParams[key as keyof ImportLeadsData] = String(value) as any;
        }
      }
    });

    try {
      const response = await importLeadsMutation.mutateAsync({
        data: uploadFormData,
        queryParams
      });
      localStorage.setItem(IMPORT_ID_STORAGE_KEY, response.importId);
      setCurrentImportId(response.importId);
      setIsProcessing(true);

      toast.success(response.message || t(`${TRANSLATION_KEY}.importStarted`));
    } catch (error: any) {
      console.error("Import error:", error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        t(`${TRANSLATION_KEY}.importError`);

      setFileError(errorMessage);
      setAttachments([]);
      uploadedFileRef.current = null;
      setHasFile(false);
    }
  };

  const canImport = hasFile && !isUploading && isFormValid;

  const formFieldsContext: CreateFormNewContext = {
    users,
    userData,
    selectedCompanyId: "",
    selectedPersonId: "",
    setSelectedCompanyId: () => {},
    setSelectedPersonId: () => {},
    setHasUserEnteredDealName: () => {},
    autoPopulateDealName: () => {},
    hasUserEnteredDealName: false,
    setView: () => {},
    entityType: EntityType.LEAD
  };

  const baseSections = createFormNewSectionsFromAPI(
    visibleFields,
    t,
    formFieldsContext
  );

  const formSections: FormSectionProps[] = useMemo(() => {
    const fieldOrder = ["productId", "source", "sdrOwner", "aeOwner"];
    const allFields = baseSections[0]?.fields || [];

    const orderedFields = fieldOrder
      .map((fieldName) => allFields.find((field) => field.name === fieldName))
      .filter((field) => field !== undefined);

    const configuredFields = orderedFields.map((field) => {
      let enhancedField = {
        ...field,
        labelPosition: "left" as const,
        layout: "flex" as const
      };

      if (field.name === "productId") {
        enhancedField.fieldActionProps = {
          fieldName: "productId",
          options: map(products, (product) => ({
            id: product.id,
            displayName: product.name
          })),
          onSelection: (value: any) => {
            setProductId(value);
          }
        };
        if (products?.length === 1 && products[0]) {
          enhancedField.defaultValue = products[0].id;
        }
      }

      if (field.name === "source") {
        const existingOptions =
          (enhancedField.fieldActionProps as any)?.options || [];
        const filteredOptions = existingOptions.filter(
          (option: any) => Number(option.id) !== LeadType.REFERRAL
        );

        enhancedField.fieldActionProps = {
          ...(enhancedField.fieldActionProps as any),
          fieldName: "source",
          options: filteredOptions,
          onSelection: (value: any) => {
            setSelectedSource(Number(value as string) as LeadType);
          }
        } as any;
      }

      if (field.name === "sdrOwner" || field.name === "aeOwner") {
        enhancedField.fieldActionProps = {
          fieldName: field.name,
          options: getUsersOptions(users),
          showSearch: true,
          renderIcon: (value: string | number) => (
            <UserAvatar userData={users[String(value)]} size="M" />
          )
        };
        if (field.name === "sdrOwner" && userData) {
          enhancedField.defaultValue = userData.id;
        }
      }

      return enhancedField;
    });

    // Inject conditional fields after the source field
    if (getConditionalFields.length > 0) {
      const conditionalFieldsWithLayout = getConditionalFields.map((field) => ({
        ...field,
        labelPosition: "left" as const,
        layout: "flex" as const
      }));

      const sourceFieldIndex = configuredFields.findIndex(
        (field) => field.name === "source"
      );

      if (sourceFieldIndex !== -1) {
        // Insert conditional fields right after the source field
        const newFields = [
          ...configuredFields.slice(0, sourceFieldIndex + 1),
          ...conditionalFieldsWithLayout,
          ...configuredFields.slice(sourceFieldIndex + 1)
        ];

        return [
          {
            id: "import-lead-fields",
            fields: newFields
          }
        ];
      }
    }

    return [
      {
        id: "import-lead-fields",
        fields: configuredFields
      }
    ];
  }, [baseSections, products, users, userData, getConditionalFields, t]);

  useEffect(() => {
    if (products?.length === 1 && products[0]?.id && !productId) {
      setProductId(products[0].id);
      if (typeof formSetValue === "function") {
        formSetValue("productId", products[0].id, {
          shouldValidate: true
        });
      }
    }
  }, [products, productId, formSetValue]);

  useEffect(() => {
    if (typeof formSetValue === "function" && selectedSource !== null) {
      formSetValue("inboundCampaignType", "", { shouldValidate: true });
      formSetValue("outboundCampaignType", "", { shouldValidate: true });
      formSetValue("referringCompany", "", { shouldValidate: true });
      formSetValue("referringPerson", "", { shouldValidate: true });

      setSelectedReferringCompanyId("");
    }
  }, [selectedSource, formSetValue]);

  useEffect(() => {
    if (isOpen) {
      const storedImportId = localStorage.getItem(IMPORT_ID_STORAGE_KEY);
      if (storedImportId) {
        setCurrentImportId(storedImportId);
        setIsProcessing(true);
      }
    } else {
      setAttachments([]);
      uploadedFileRef.current = null;
      setHasFile(false);
      setFileError(null);
      setProductId("");
      setSelectedSource(null);
      setSelectedReferringCompanyId("");
      setIsFormValid(false);
      formRef.current?.reset();
    }
  }, [isOpen]);

  useEffect(() => {
    if (importStatus.data) {
      const { status } = importStatus.data;

      if (status === "completed" || status === "failed") {
        setIsProcessing(false);
        localStorage.removeItem(IMPORT_ID_STORAGE_KEY);
        setCurrentImportId("");

        if (status === "completed") {
          toast.success(
            t(`${TRANSLATION_KEY}.importCompleted`, {
              count: importStatus.data.createdCount
            })
          );
        } else {
          toast.error(t(`${TRANSLATION_KEY}.importFailed`));
        }
      }
    }
  }, [importStatus.data, t]);

  useEffect(() => {
    if (importStatus.error && currentImportId) {
      console.error("Import status error:", importStatus.error);
      setIsProcessing(false);
      localStorage.removeItem(IMPORT_ID_STORAGE_KEY);
      setCurrentImportId("");
    }
  }, [importStatus.error, currentImportId]);

  const handleSetValue = (formMethods: { setValue: any; getValues: any }) => {
    if (typeof formMethods.setValue === "function") {
      setFormSetValue(() => formMethods.setValue);
    }
    if (typeof formMethods.getValues === "function") {
      setFormGetValues(() => formMethods.getValues);
    }
  };

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      data-test="leads-import-modal"
      title={
        <Text
          as="div"
          variant="lgSubtitleH1"
          data-test="leads-import-modal-title"
        >
          {t(`${TRANSLATION_KEY}.title`)}
        </Text>
      }
      content={
        <div
          className="flex flex-col gap-8 w-[45rem] p-6"
          data-test="leads-import-modal-content"
        >
          {isProcessing ? (
            <ImportProcessingContent
              emailsCount={importStatus.data?.totalEmails || 0}
            />
          ) : (
            <>
              <div className="flex flex-col gap-6">
                <Text>{t(`${TRANSLATION_KEY}.description`)}</Text>
                <FileUpload
                  onRawFileSelect={(file) => {
                    uploadedFileRef.current = file;
                    setHasFile(true);
                    setFileError(null);
                  }}
                  onAttachmentsChange={setAttachments}
                  onUploadingChange={setIsUploading}
                  externalError={fileError}
                  initialAttachments={attachments}
                  isSingleFile={true}
                  dragAndDropText={t(`${TRANSLATION_KEY}.dnd`)}
                  description={
                    <>
                      {t(`${TRANSLATION_KEY}.fileDescription`)}
                      <br />
                      {t(`${TRANSLATION_KEY}.fileDescriptionNote`)}
                    </>
                  }
                  accept=".csv"
                  showFileInFullSize
                />
              </div>
              <div className="mb-6">
                <FormNew
                  sections={formSections}
                  isFormValid={(
                    isValid: boolean,
                    watchAllFields: Record<string, any>
                  ) => {
                    const hasConditionalFieldsBeenFilled =
                      getHasConditionalFieldsBeenFilled(watchAllFields);
                    setIsFormValid(isValid && hasConditionalFieldsBeenFilled);
                  }}
                  formRef={formRef}
                  onSetValue={handleSetValue}
                  shouldNotResetOnSubmit={true}
                />
              </div>
            </>
          )}
        </div>
      }
      footer={
        <div
          className="flex justify-end gap-2"
          data-test="leads-import-modal-footer"
        >
          {!isProcessing && (
            <Button
              data-test="leads-import-modal-cancel-button"
              variant="secondary"
              onClick={handleClose}
            >
              {t("cancel")}
            </Button>
          )}
          {isProcessing ? (
            <Button
              data-test="leads-import-modal-close-button"
              onClick={handleClose}
            >
              {t("close")}
            </Button>
          ) : (
            <Button
              data-test="leads-import-modal-import-button"
              disabled={!canImport || importLeadsMutation.isPending}
              onClick={handleImport}
            >
              {t(`${TRANSLATION_KEY}.import`)}
            </Button>
          )}
        </div>
      }
    />
  );
};

export default ImportLeadsModal;
